import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, Link } from '@mui/material';
import { ArticleRounded, OpenInNewRounded } from '@mui/icons-material';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import AndieGenericToolRender from './AndieGenericToolRender';

type ActionResponse = { url?: string; title?: string; markdown?: string };
type ActionArgs = { url?: string; withLinks?: boolean };

export type AndieReadUrlProps = ActionRenderAi<ActionResponse, ActionArgs>;

const SNIPPET_CHARS = 280;

const domainOf = (url?: string): string => {
    if (!url) return '';
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url.replace(/^https?:\/\//, '').split('/')[0];
    }
};

// Strip markdown syntax to a plain-text snippet for the preview line.
const toSnippet = (markdown?: string): string => {
    if (!markdown) return '';
    const plain = markdown
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '') // images
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> text
        .replace(/[#>*_`~-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return plain.length > SNIPPET_CHARS ? `${plain.slice(0, SNIPPET_CHARS)}…` : plain;
};

// Stable module-level component (NOT an inline render prop) so AndieGenericToolRender updates this
// subtree instead of remounting it on every streamed re-render. Renders only once complete.
const ReadUrlPreview: React.FC<ActionRenderAi<ActionResponse, ActionArgs>> = ({ status, result, args }) => {
    if (status !== ActionResponseStatusAi.COMPLETE) return null;
    const url = result?.url || args?.url;
    const snippet = toSnippet(result?.markdown);
    if (!url && !snippet) return null;

    return (
        <Stack gap={0.5} sx={{ py: 0.5, pr: 0.5 }}>
            {url && (
                <Link
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 0,
                        color: 'andie.main',
                    }}
                >
                    <Typography
                        component="span"
                        variant="caption"
                        fontWeight={600}
                        noWrap
                        sx={{ color: 'inherit' }}
                    >
                        {result?.title?.trim() || domainOf(url)}
                    </Typography>
                    <OpenInNewRounded sx={{ fontSize: 12, flexShrink: 0 }} />
                </Link>
            )}
            {snippet && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {snippet}
                </Typography>
            )}
        </Stack>
    );
};

const AndieReadUrl = memo((props: AndieReadUrlProps) => {
    const { t } = useTranslation();

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.read-url.title')}
            icon={<ArticleRounded fontSize="small" />}
            loadingMessages={(args) => [
                t('andie.tool.read-url.loading', { value: domainOf(args?.url) || '…' }),
            ]}
            completeMessage={({ result, args }) =>
                result?.title?.trim() ||
                domainOf(result?.url || args?.url) ||
                t('andie.tool.read-url.complete')
            }
            additionalComponent={ReadUrlPreview}
            autoExpandOnComplete={false}
            {...props}
        />
    );
});

AndieReadUrl.displayName = 'AndieReadUrl';

export default AndieReadUrl;
